import "./UserProfile.css";
import MutedText from "../common/MutedText";
import Footer from "../common/Footer";
import PageLabel from "../common/PageLabel";
import EnrolledFactorList from "./EnrolledFactorList";
import TwoStepVerification from "./TwoStepVerification";
import {
  MFA_FACTORS_BLACK_LIST,
  PRIVACY_POLICY_URL,
  TERMS_OF_USE_URL,
  factorDisplayNameMap,
  normalizeFactorType,
} from "../../lib/constants";
import React, { useEffect, useContext, useState, useMemo } from "react";
import { getEnrolledFactors } from "../../api/Api";
import { useTranslation } from "react-i18next";
import { useOktaAuth } from "@okta/okta-react";
import { Button, Banner, BannerItem } from "@athena/forge";
import {
  logEditPassword,
  logEditFactors,
  logEditPrivacySettings,
  logLandingEvent,
} from "../../lib/amplitude-utils";
import { GlobalBannerContext } from "../../providers/GlobalBannerContext";
import { useLocation } from "react-router-dom";

export const UserProfile = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { authState } = useOktaAuth();
  const [enrolledFactorList, setEnrolledFactorList] = useState<
    { id: string; factorName: string; additionalInfo: string }[]
  >([]);
  const { globalBanner, setGlobalBanner, globalBannerType, setGlobalBannerType } = useContext(GlobalBannerContext);

  const { state } = useLocation();
  if (state && state.isPhrUser === true) {
    setGlobalBanner(t("userprofile.phruser.banner"));
    setGlobalBannerType("info");
  }

  const isAthenistaUser = useMemo(() => {
    if (!authState?.idToken) return false;

    try {
      const tokenPayload = JSON.parse(
        atob(authState.idToken.idToken.split(".")[1]),
      );
      const username = tokenPayload.preferred_username || tokenPayload.sub || tokenPayload.email || "";
      return username.endsWith("@athenahealth.com");
    } catch (error) {
      return false;
    }
  }, [authState]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const enrolledFactorsAPI = await getEnrolledFactors(authState);

      const maskPhoneNumber = (phoneNumber: string | undefined): string => {
        if (!phoneNumber) return "";
        return phoneNumber;
      };

      // Format the factor data get from API to fit the component parameter interface.
      // factorTypes are normalized before lookup — see normalizeFactorType.
      const enrolledFactorList = enrolledFactorsAPI
        .filter((f) => !MFA_FACTORS_BLACK_LIST.includes(normalizeFactorType(f.factorType)))
        .map((factor) => {
          const canonicalType = normalizeFactorType(factor.factorType);
          let additionalInfo = "";
          if (canonicalType === "webauthn") {
            additionalInfo = factor.name ?? "";
          }
          else if (factor.profile?.phoneNumber) {
            additionalInfo = maskPhoneNumber(factor.profile.phoneNumber);
          }
          return {
            id: factor.id,
            factorName: t(factorDisplayNameMap[canonicalType]),
            additionalInfo,
          };
        })
        .sort((a, b) => a.factorName.localeCompare(b.factorName));

      setEnrolledFactorList(enrolledFactorList);
    };
    fetchData();
  }, []);

  useEffect(() => {
    logLandingEvent({});
  }, []);

  useEffect(() => {
    const bannerMessage = sessionStorage.getItem("globalBanner");
    const bannerType = sessionStorage.getItem("globalBannerType");
    if (bannerMessage) {
      setGlobalBanner(bannerMessage);
      setGlobalBannerType(bannerType as "info" | "attention" | "critical" | "new" | "success" | undefined);
      sessionStorage.removeItem("globalBanner");
      sessionStorage.removeItem("globalBannerType");
    }
  }, [setGlobalBanner, setGlobalBannerType]);

  const footerLinks = [
    {
      text: t("userprofile.footerlink.termofuse"),
      externalLink: TERMS_OF_USE_URL,
    },
    {
      text: t("userprofile.footerlink.privacypolicy"),
      externalLink: PRIVACY_POLICY_URL,
    },
  ];

  return (
    <>
      <PageLabel
        text={t("userprofile.pagelabel.athenahealthprofile")}
        fontSize="xlarge"
      />
      { }

      {globalBanner && (
        <div className="fe_u_margin--top-medium fe_u_margin--bottom-medium">
          <Banner
            alertType={globalBannerType}
            onHide={() => setGlobalBanner("")}>
            <BannerItem
              headerText={globalBanner}
              headingTag="h1"
              className="fe_u_font-bold"
            />
          </Banner>
        </div>
      )}

      {!isAthenistaUser && (
        <>
          <PageLabel
            text={t("userprofile.pagelabel.securitysettings")}
            fontSize="large"
          />
          <MutedText
            className="fe_u_font-size--small fe_u_text-align--left fe_u_margin--right-medium"
            text={t("userprofile.description.securitysettings")}
          />

          <div className="uiam-user-profile-section-container fe_u_margin--top-medium fe_u_margin--bottom-medium">
            <div>
              <div>{t("userprofile.sectiontitle.password")}</div>
              <div>********</div>
            </div>
            <Button
              data-testid="edit-password-button-test"
              text={t("userprofile.button.edit")}
              variant="secondary"
              size="medium"
              onClick={() => {
                logEditPassword();
                window.location.href = "/editpassword";
              }}
            />
          </div>

          <div className="uiam-user-profile-section-container ">
            <div className="fe_u_margin--bottom-small">
              <div>{t("userprofile.sectiontitle.verificationmethods")}</div>
            </div>
            <Button
              data-testid="verfication-methods-button-test"
              text={t("userprofile.button.edit")}
              variant="secondary"
              size="medium"
              onClick={() => {
                logEditFactors();
                window.location.href = "/managefactors";
              }}
            />
          </div>
          <div className="uiam-verification-method-description">
            <MutedText
              className="fe_u_font-size--small fe_u_text-align--left"
              text={t("userprofile.verificationmethod.description")}
            />
            <EnrolledFactorList enrolledFactorList={enrolledFactorList} />
          </div>

          <TwoStepVerification />

          <div className="okta-form-divider form-divider fe_u_margin--bottom-medium fe_u_margin--top-medium"></div>
        </>
      )}

      <PageLabel
        text={t("userprofile.pagelabel.privacysettings")}
        fontSize="large"
      />
      <div className="uiam-user-profile-section-container ">
        <MutedText
          className="fe_u_font-size--small fe_u_text-align--left fe_u_margin--right-medium"
          text={t("userprofile.privacysettings.description")}
        />
        <Button
          data-testid="privacy-settings-button-test"
          text={t("userprofile.button.edit")}
          variant="secondary"
          size="medium"
          onClick={() => {
            logEditPrivacySettings();
            window.location.href = "/privacysettings";
          }}
        />
      </div>

      <Footer footerLinks={footerLinks}></Footer>
    </>
  );
};
